'use client';
import React, { useState, useRef } from 'react';
import { Upload, FileText, Loader2, AlertCircle } from 'lucide-react';
import { ParsedProfile } from './OnboardingWizard';

interface Step1UploadProps {
  onParsed: (profile: ParsedProfile) => void;
  expertId: string | null;
  initLoading: boolean;
}

export default function Step1Upload({ onParsed, expertId, initLoading }: Step1UploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    if (f.type !== 'application/pdf') {
      setError('Please upload a PDF file only.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('File size must be under 10MB.');
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  const handleParse = async () => {
    if (!file || !expertId) return;

    setParsing(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('expert_id', expertId);

      const response = await fetch('/api/parse-cv', {
        method: 'POST',
        body: form,
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || `CV parsing failed (${response.status})`);
      }

      onParsed(data.profile as ParsedProfile);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to parse CV. Please try again.';
      setError(message);
    } finally {
      setParsing(false);
    }
  };

  const buttonDisabled = !file || parsing || initLoading || !expertId;

  return (
    <div className="surface-elevated p-8 flex flex-col gap-6">
      <div>
        <h2 className="font-heading text-xl text-foreground mb-1">Upload Your CV</h2>
        <p className="text-sm text-muted-foreground">
          Our AI will extract expertise signals and permanently mask all identifying information.
          Your real name, employer, and institution will never appear publicly.
        </p>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !parsing && fileRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-10 flex flex-col items-center gap-4 cursor-pointer
          transition-all duration-200
          ${dragOver ? 'border-primary/60 bg-primary/5' : 'border-border hover:border-primary/30 hover:bg-muted/30'}
          ${parsing ? 'cursor-not-allowed opacity-60' : ''}
        `}
        role="button"
        aria-label="Upload CV file"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && !parsing && fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          disabled={parsing}
          aria-label="CV file input"
        />

        {parsing ? (
          <>
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 size={28} className="text-primary animate-spin" />
            </div>
            <div className="text-center">
              <p className="font-medium text-foreground mb-1">Parsing with AI…</p>
              <p className="text-sm text-muted-foreground">
                Extracting expertise, masking identity. This takes about 30 seconds.
              </p>
            </div>
            <div className="w-full max-w-xs bg-muted rounded-full h-1.5 overflow-hidden">
              <div className="bg-primary h-full rounded-full animate-pulse" style={{ width: '65%' }} />
            </div>
          </>
        ) : file ? (
          <>
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText size={28} className="text-primary" />
            </div>
            <div className="text-center">
              <p className="font-medium text-foreground">{file.name}</p>
              <p className="text-sm text-muted-foreground">
                {(file.size / 1024).toFixed(0)} KB — Ready to parse
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <Upload size={28} className="text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-medium text-foreground">Drop your CV here</p>
              <p className="text-sm text-muted-foreground">PDF only · Max 10MB · Click to browse</p>
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="flex items-start gap-3 p-4 rounded-lg bg-secondary/5 border border-secondary/15">
        <span className="text-secondary mt-0.5">🔒</span>
        <div>
          <p className="text-sm font-medium text-foreground mb-0.5">Privacy guarantee</p>
          <p className="text-xs text-muted-foreground">
            Your CV is processed in an isolated context. Real name, employer, and institution are
            stored in encrypted protected fields and never returned to any buyer-facing endpoint.
          </p>
        </div>
      </div>

      <button
        onClick={handleParse}
        disabled={buttonDisabled}
        className="w-full py-3 px-6 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {parsing ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Parsing CV…
          </>
        ) : initLoading ? (
          'Setting up…'
        ) : (
          'Parse CV with AI'
        )}
      </button>
    </div>
  );
}
